import { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

type PdfPagesResult = {
  pages: string[];
  loading: boolean;
  error: boolean;
};

/**
 * Renders every page of a PDF document into an image (data URL) so the real
 * pages can be displayed as styled "paper sheet" previews.
 */
export function usePdfPages(url: string): PdfPagesResult {
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const renderPages = async () => {
      setLoading(true);
      setError(false);

      try {
        const doc = await pdfjsLib.getDocument({ url }).promise;
        const rendered: string[] = [];

        for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
          const page = await doc.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          const context = canvas.getContext('2d');
          if (context) {
            await page.render({ canvasContext: context, viewport }).promise;
            // Lossless PNG keeps the original page colors accurate (no JPEG color shift).
            rendered.push(canvas.toDataURL('image/png'));
          }
        }

        if (!cancelled) {
          setPages(rendered);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setPages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    renderPages();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { pages, loading, error };
}